// durationDisplay.js — the 8-hour shop day.
//
// Duration is STORED as a decimal number of DAYS and that never changes. This
// module is the only place that knows a shop day is 8 hours, and the only place
// that converts between days and hours for display and entry.
//
// Read side CEILS, write side TRUNCATES — see hoursToDays() for why.
//
// Zero imports on purpose: scripts/test/durationDisplay.test.mjs loads this
// directly under Node's ESM resolver, with no webpack.

export const HOURS_PER_DAY = 8;

/**
 * Snap a duration to the 2 decimals it is stored with.
 *
 * Every duration column is decimal(_,2), so this is lossless — but a line's
 * `duration_days * quantity` is floating point: 0.8 * 3 is 2.4000000000000004,
 * and that noise reaches the screen wherever a value is rendered raw.
 */
export function round2(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return 0;
  return Math.round(n * 100) / 100;
}

/** Round a decimal day count UP to whole hours. 0.2d -> 1.6h -> 2h. */
export function daysToHours(days) {
  const n = Number(days);
  if (!Number.isFinite(n) || n <= 0) return 0;
  // toFixed(10) first: binary floating point can overshoot a product that is
  // mathematically a whole number of hours, and a bare Math.ceil would then add
  // a spurious hour. Snapping to 10 decimals first removes that class of error.
  return Math.ceil(Number((n * HOURS_PER_DAY).toFixed(10)));
}

/** Drop insignificant zeros: 1.00 -> '1', 1.50 -> '1.5', 1.25 -> '1.25'. */
function trim(n) {
  return String(Math.round(Number(n) * 100) / 100);
}

// Always spelled out. A one-letter suffix was tried and rejected: these cells
// render in a mono face, where 'h' and 'd' share an advance width and '4h' next
// to '4d' is genuinely hard to tell apart.
function hourText(hours) {
  const v = trim(hours);
  return `${v} hr${Number(v) === 1 ? '' : 's'}`;
}

function dayText(days) {
  const v = trim(days);
  return `${v} day${Number(v) === 1 ? '' : 's'}`;
}

/**
 * Format a stored duration for display.
 *
 * @param {number|string|null} value  the stored number
 * @param {'days'|'hours'} unit       the row's duration_unit. When 'hours' the
 *                                    number IS hours and is never multiplied.
 * @returns {string|null}  null for empty input, so each call site keeps its own
 *                         placeholder ('——', '—', '-'). Zero is NOT empty.
 */
export function formatDuration(value, unit = 'days') {
  if (value === null || value === undefined || value === '') return null;
  const n = Number(value);
  if (!Number.isFinite(n)) return null;

  if (unit === 'hours') return hourText(n);
  if (n < 1) return hourText(daysToHours(n));
  return dayText(n);
}

/** Hours mode covers under a day only. At or above 8 hours you enter days. */
export const MAX_ENTRY_HOURS = HOURS_PER_DAY;

/** Days mode granularity. 0.25 keeps every production value reachable (1.25). */
export const DAY_STEP = 0.25;

/**
 * Convert whole hours to the decimal-day value we STORE.
 *
 * Truncates at 2 decimals rather than dividing. Math.ceil on the read side
 * gives each whole hour a band of day-values ~0.125 wide; two decimals is fine
 * enough that every band contains a representable value, and this picks the
 * largest one inside the band. The round trip is then exact.
 *
 * 0.12 is not "1 hour rounded badly" — it is the canonical 2dp representative
 * of the one-hour band. Naive hours/8 gives 0.125, which decimal(4,2) stores as
 * 0.13, which reads back as 2 hrs.
 */
export function hoursToDays(hours) {
  const n = Number(hours);
  if (!Number.isFinite(n) || n <= 0) return 0;
  return Math.floor((n * 100) / HOURS_PER_DAY) / 100;
}

/** Which unit a field opens in — the same boundary display uses. */
export function initialUnit(days) {
  const n = Number(days);
  if (!Number.isFinite(n) || n <= 0) return 'hours';
  return n < 1 ? 'hours' : 'days';
}

/** The number to show in the field, given the stored days and the chosen unit. */
export function toInputValue(days, unit) {
  if (days === null || days === undefined || days === '') return '';
  const n = Number(days);
  if (!Number.isFinite(n)) return '';
  // Deliberately NOT clamped to MAX_ENTRY_HOURS. Showing 24 against max=8 marks
  // the field invalid and prompts a correction; clamping would silently discard
  // the user's 3-day value the moment they tapped the unit toggle.
  return unit === 'hours' ? daysToHours(n) : round2(n);
}

/** The decimal-day value to emit, given what the user typed and the unit. */
export function fromInputValue(raw, unit) {
  if (raw === null || raw === undefined || raw === '') return '';
  const n = Number(raw);
  if (!Number.isFinite(n)) return '';
  return unit === 'hours' ? hoursToDays(n) : n;
}

/**
 * Format a span already measured in MINUTES — mobile appointments and slots.
 *
 * Separate from formatDuration on purpose. A mobile booking is placed on a
 * 30-minute grid, so it needs minute precision; formatDuration ceils to whole
 * hours and would call a 90-minute appointment "2 hrs". Same vocabulary, finer
 * resolution.
 *
 * @param {number|string|null} totalMinutes
 * @returns {string|null}  null for empty input, as formatDuration does.
 */
export function formatMinutes(totalMinutes) {
  if (totalMinutes === null || totalMinutes === undefined || totalMinutes === '') return null;
  const n = Number(totalMinutes);
  if (!Number.isFinite(n) || n < 0) return null;

  const mins = Math.round(n);
  const h = Math.floor(mins / 60);
  const m = mins % 60;

  if (h === 0) return `${m} min`;
  const hourPart = `${h} hr${h === 1 ? '' : 's'}`;
  // No bare "0m" tail: a days-based service is always a whole number of hours,
  // so every mobile booking used to read "4h 0m".
  return m === 0 ? hourPart : `${hourPart} ${m} min`;
}
